import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../components/Notification';
import {
  formatReferralRewardSentence,
  formatReferralRewardShort,
  normalizeReferralRewards,
} from '../utils/referralRewards';

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-3xl sm:text-4xl font-bold tabular-nums mt-2 ${accent || 'text-gray-900 dark:text-white'}`}>
        {value}
      </p>
      {sub && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

const Referral = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const fetchGenRef = useRef(0);

  const fetchReferral = useCallback(async ({ silent = false } = {}) => {
    if (!user?._id) {
      setData(null);
      setLoading(false);
      return;
    }
    const gen = ++fetchGenRef.current;
    if (!silent) setLoading(true);
    try {
      const { data: res } = await api.get('/referrals/me');
      if (gen !== fetchGenRef.current) return;
      setData(res);
    } catch (error) {
      if (gen !== fetchGenRef.current) return;
      console.error('Error fetching referral data:', error);
      setData(null);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    fetchReferral();
  }, [fetchReferral]);

  const copyLink = async () => {
    const link = data?.referralLink;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      showNotification('Referral link copied!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showNotification('Could not copy link', 'error');
    }
  };

  const copyCode = async () => {
    const code = data?.referralCode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      showNotification('Referral code copied!', 'success');
    } catch {
      showNotification('Could not copy code', 'error');
    }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  const rewards = normalizeReferralRewards(data?.rewardSettings || data?.stats || {});
  const referrals = data?.referrals || [];
  const verified = data?.stats?.verifiedReferrals ?? 0;
  const towardNext = data?.stats?.towardNext ?? (rewards.referralsRequired > 1 ? verified % rewards.referralsRequired : 0);
  const remainingToNext =
    data?.stats?.remainingToNext ??
    (rewards.referralsRequired > 1
      ? towardNext === 0
        ? rewards.referralsRequired
        : rewards.referralsRequired - towardNext
      : 1);
  const progressPct =
    rewards.referralsRequired > 1
      ? Math.min(100, Math.round((towardNext / rewards.referralsRequired) * 100))
      : 0;
  const capLimit = data?.cap?.limit ?? data?.stats?.maxReferrals ?? 10;
  const capUsed = data?.cap?.used ?? data?.stats?.totalReferred ?? 0;
  const capRemaining = data?.cap?.remaining ?? Math.max(0, capLimit - capUsed);
  const atCap = Boolean(data?.cap?.atCap) || !data?.linkActive;
  const capPct = capLimit > 0 ? Math.min(100, Math.round((capUsed / capLimit) * 100)) : 100;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Referrals</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {formatReferralRewardSentence(rewards)}
          </p>
        </div>

        {atCap ? (
          <div className="mb-6 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-5">
            <p className="text-base font-semibold text-amber-900 dark:text-amber-100">
              Referral limit reached ({capUsed}/{capLimit})
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
              Your referral link is paused. New visitors who open your link can still register normally on
              WeRgame, but they will not count as your referrals. Open a Discord ticket to request a higher
              limit.
            </p>
          </div>
        ) : null}

        <div className={`mb-8 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 sm:p-8 text-white ${atCap ? 'opacity-90' : ''}`}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium opacity-90 uppercase tracking-wide">Your referral link</p>
          <p className="text-sm opacity-80 mt-1 mb-3">
            {atCap
              ? 'Link inactive for new referral credit. Share after your limit is increased.'
              : (
                <>
                  Share this link. Friends must sign up <strong className="text-white">and</strong> make a free
                  prediction before they count toward your reward.
                </>
              )}
          </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      readOnly
                      value={data?.referralLink || ''}
                      className="flex-1 min-w-0 px-4 py-2.5 rounded-lg bg-white/15 border border-white/25 text-white text-sm font-mono placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/40"
                      aria-label="Referral link"
                    />
                    <button
                      type="button"
                      onClick={copyLink}
                      disabled={atCap}
                      className="shrink-0 px-5 py-2.5 rounded-lg bg-white text-violet-700 font-semibold text-sm hover:bg-violet-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {copied ? 'Copied!' : atCap ? 'Paused' : 'Copy link'}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                    <span className="opacity-80">Code:</span>
                    <button
                      type="button"
                      onClick={copyCode}
                      disabled={atCap}
                      className="font-mono font-semibold px-2 py-0.5 rounded bg-white/15 hover:bg-white/25 transition-colors disabled:opacity-50"
                      title="Copy code"
                    >
                      {data?.referralCode || '—'}
                    </button>
                    <span className="opacity-80 ml-2">
                      Limit: {capUsed}/{capLimit}
                      {capRemaining > 0 ? ` · ${capRemaining} remaining` : ''}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 text-center lg:text-right max-w-xs lg:max-w-[14rem]">
                  <p className="text-sm opacity-90">Reward</p>
                  <p className="text-5xl font-bold tabular-nums mt-1">🎫 {rewards.goldenTickets}</p>
                  <p className="text-sm opacity-90 mt-1 leading-snug">{formatReferralRewardShort(rewards)}</p>
                </div>
              </div>
            </div>

            <div className="mb-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
              <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Referral limit</p>
                <p className="text-xs tabular-nums text-gray-600 dark:text-gray-300">
                  {capUsed} of {capLimit} used
                </p>
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${atCap ? 'bg-amber-500' : 'bg-violet-600'}`}
                  style={{ width: `${capPct}%` }}
                  aria-hidden
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Admin-set max referrals for your account
                {data?.cap?.hasOverride ? ' (custom limit)' : ` (default ${data?.cap?.globalMaxReferrals ?? 10})`}.
                {atCap
                  ? ' Request an upgrade via Discord ticket to invite more friends.'
                  : ` You can still invite ${capRemaining} more.`}
              </p>
            </div>

            {rewards.enabled && rewards.referralsRequired > 1 ? (
              <div className="mb-8 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/80 dark:bg-violet-950/30 p-5">
                <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
                  <p className="text-sm font-semibold text-violet-900 dark:text-violet-100">
                    Progress to next reward
                  </p>
                  <p className="text-xs tabular-nums text-violet-800 dark:text-violet-200">
                    {towardNext} of {rewards.referralsRequired} verified
                  </p>
                </div>
                <div className="h-2 rounded-full bg-violet-200/80 dark:bg-violet-900/60 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-violet-600 dark:bg-violet-400 transition-all"
                    style={{ width: `${progressPct}%` }}
                    aria-hidden
                  />
                </div>
                <p className="text-xs text-violet-800/90 dark:text-violet-200/90 mt-2">
                  {remainingToNext} more verified player{remainingToNext === 1 ? '' : 's'} until you earn{' '}
                  {rewards.goldenTickets} golden ticket{rewards.goldenTickets === 1 ? '' : 's'}.
                </p>
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <StatCard
                label="Total referred"
                value={data?.stats?.totalReferred ?? 0}
                sub="Users who joined with your link"
              />
              <StatCard
                label="Verified referrals"
                value={verified}
                sub="Completed first free prediction"
              />
              <StatCard
                label="Golden tickets earned"
                value={data?.stats?.goldenTicketsFromReferrals ?? 0}
                sub="From verified referral rewards"
                accent="text-amber-600 dark:text-amber-400"
              />
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Referred users</h2>
                <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  {referrals.length} total
                </span>
              </div>

              {referrals.length === 0 ? (
                <div className="p-10 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
                    No referrals yet. Copy your link above and share it with friends. {formatReferralRewardSentence(rewards)}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          User
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                          Joined
                        </th>
                        <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Status
                        </th>
                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Tickets earned
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {referrals.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{row.username}</p>
                            {row.email && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px] sm:max-w-xs">
                                {row.email}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-400 sm:hidden mt-0.5">
                              {formatDate(row.joinedAt)}
                            </p>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell whitespace-nowrap">
                            {formatDate(row.joinedAt)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            {row.status === 'verified' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                Verified
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-sm text-right font-semibold tabular-nums text-amber-600 dark:text-amber-400">
                            {row.status === 'verified' ? `+${row.goldenTicketsAwarded}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
              Golden tickets can be used for boost predictions. Check your balance on your{' '}
              <Link to="/profile" className="text-violet-600 dark:text-violet-400 font-medium hover:underline">
                profile
              </Link>
              .
            </p>
      </div>
    </div>
  );
};

export default Referral;
